import { Theme } from '@mui/material/styles'

import { reducedMotionOverrides } from '../motion'
import radii from '../radii'
import { layout } from '../tokens.generated'

// ----------------------------------------------------------------------
// Surface-wide rules that belong to the system rather than to a component:
// the focus ring every interactive element gets, the one reduced-motion
// block, and the vendor controls we do not render but do have to answer for.
// All of them are here so honouring them is not a per-component decision.
// ----------------------------------------------------------------------

export default function CssBaseline(theme: Theme) {
  return {
    MuiCssBaseline: {
      styleOverrides: {
        ...reducedMotionOverrides,
        '*': {
          margin: 0,
          padding: 0,
          boxSizing: 'border-box',
          // Thin, and on every element, because `scrollbar-width` does not
          // inherit (the colour is on `html` below). 11px instead of 15 at
          // 100% zoom, so a bar takes less of a narrow card. A component that
          // hides its bar (`Scrollbar`, the horizontal navigation) says
          // `none` on a class, which outranks this.
          scrollbarWidth: 'thin',
        },
        // A visible focus ring on everything a keyboard can reach. `focus-visible`
        // rather than `focus`, so a pointer click does not leave a ring behind.
        // Never removed without a replacement, and never replaced by a colour
        // change alone: a tab, a segmented cell and a sortable column head
        // replace it with their pointer's underline (`STRIP_FOCUS`, 2026-11-01).
        ':focus-visible': {
          outline: `${layout.focusRingWidth}px solid ${theme.palette.accentRing}`,
          outlineOffset: 1,
        },
        'html': {
          width: '100%',
          height: '100%',
          WebkitOverflowScrolling: 'touch',
          // **A scrollbar that takes room is drawn by the console, not left
          // to the platform.** Windows, and a Mac set to always show scroll
          // bars, draw a 15px bar that takes its width out of whatever
          // scrolls. The platform's own is a light grey track with arrows,
          // and it stayed light in dark mode: a white strip down a dark
          // dialog and down the navigation. The thumb is its own token role
          // on a transparent track, so the bar sits on the surface it
          // scrolls in both schemes. `scrollbar-color` inherits, so set here
          // it reaches every scroller, the portaled dialogs included.
          // Overlay scroll bars (a Mac's default, phones) ignore both
          // properties and are unchanged.
          scrollbarColor: `${theme.palette.scrollbarThumb} transparent`,
        },
        'body': {
          width: '100%',
          height: '100%',
        },
        '#root': {
          width: '100%',
          height: '100%',
        },
        'input': {
          '&[type=number]': {
            'MozAppearance': 'textfield',
            '&::-webkit-outer-spin-button': {
              margin: 0,
              WebkitAppearance: 'none',
            },
            '&::-webkit-inner-spin-button': {
              margin: 0,
              WebkitAppearance: 'none',
            },
          },
        },
        'img': {
          display: 'block',
          maxWidth: '100%',
        },
        // mapbox-gl draws its attribution toggle as a 24px disc on a 12px
        // radius, which is a circle. On a narrow viewport the map collapses
        // the attribution to that toggle, so the fleet map was the one view
        // shipping a third button shape, and it was a vendor's.
        //
        // It is restyled and never removed: Mapbox's terms require the
        // attribution to stay reachable. The control radius is the console's
        // answer for anything a pointer presses.
        //
        // **The type selector is the mechanism, not decoration.** Mapbox
        // declares this on `.mapboxgl-ctrl-attrib-button`, and in dev Vite
        // injects `mapbox-gl.css` after every emotion tag, so a rule of equal
        // weight loses the tie on source order and the control still computes
        // 12px. `button.mapboxgl-ctrl-attrib-button` outranks it whatever the
        // order turns out to be. Measured in the running preview, where the
        // equal-weight version read as fixed in the file and 12px on the page.
        'button.mapboxgl-ctrl-attrib-button': {
          borderRadius: radii.control,
        },
        // The Mapbox wordmark is a link, and it was the one pressable on a
        // map that did not answer the pointer. It stays (Mapbox's terms), so
        // it answers the way a console control does, with the hover tint on
        // the inline radius. The type selector for the reason given above:
        // the vendor sheet can load after this one.
        'a.mapboxgl-ctrl-logo:hover': {
          backgroundColor: theme.palette.action.hover,
          borderRadius: radii.inline,
        },
      },
    },
  }
}
